import React, {ReactElement, useEffect, useState} from 'react';
import axios from 'axios';
import Input from 'ui/Input';
import Modal from 'ui/Modal';
import reCaptcha from 'utils/reCaptcha';
import service from 'error-handler/functions/service';
import {emptyValidator} from 'utils/validator';
import {GOOGLE_KEY, host} from 'utils/constants';
import {getCookie, getSearchParameters} from 'utils/helper';
import RootLayout from 'layouts/RootLayout';
import {apiMap} from 'api/apiMap';
import {useRouter} from 'next/router';
import {useDispatch} from 'react-redux';
import {setLogged, setUser} from 'redux/actionCreators/userActions';
import Link from 'next/link';
import {withSessionSsr} from 'lib/withSession';
import {useCookies} from "react-cookie";
import Telegram from "../../../error-handler/helper/Telegram";

interface IParams {
  login: string;
}

interface IFormData {
  login: string;
  password: string;
}

export default function LogInPage() {
  const [{}, setCookie, remove] = useCookies(['user', 'flash_message']);
  const [loaderForm, setLoaderForm] = useState<boolean>(false);
  const [formData, setFormData] = useState<IFormData>({login: '', password: ''});
  const [errors, setErrors] = useState<IFormData>({login: '', password: ''});
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>('');
  const [params, setParams] = useState<IParams | null>(null);
  const router = useRouter();

  const dispatch = useDispatch();

  const validator = (): boolean => {
    const loginValidator: boolean = emptyValidator({
      formData,
      setErrors,
      key: 'login',
      minChar: 2,
    });
    const passwordValidator: boolean = emptyValidator({
      formData,
      setErrors,
      key: 'password',
      minChar: 4,
    });
    return loginValidator && passwordValidator;
  };

  useEffect(() => {
    reCaptcha();
    dispatch(setLogged(false));
    const params = getSearchParameters();
    if (params) setParams(params as IParams);
    else setParams({login: 'nofirst'});
  }, []);

  const submitHandler = async (e: any) => {
    e.preventDefault();
    if (!validator()) return;
    setLoaderForm(true);
    let tokenRes: any;

    await grecaptcha.execute(GOOGLE_KEY, {action: 'submit'}).then((token: any) => (tokenRes = token));

    await axios
      .post(apiMap.authenticate, {...formData, 'g-recaptcha-response': tokenRes})
      .then((res: any) => {
        const {data} = res.data;
        if (res.status === 200) {
          const user = {
            company: data.company_id.name,
            logo: data.company_id.logo,
            validity: data.company_id.validity,
            first_name: data.profile_id.first_name,
            type: data.company_id.account_type,
            name: `${data.profile_id.first_name} ${data.profile_id.last_name}`,
            email: data.profile_id.email,
            phone: data.profile_id.phone,
            isAdmin: data.is_admin,
            hash_id: data.hash_id,
            hash: data.hash,
          }

          dispatch(setUser(user));
          setCookie('user', user, {path: '/'});
          dispatch(setLogged(true));
        }
        router.push(data.is_admin || data.hash_id !== null ? '/directory' : '/directory/virtual-office');
      })
      .catch((error) => {
        if (error.response.status === 401) {
          const message = `Login or password is incorrect.${host === 'loc' ? ' Or something is wrong!' : ' Try again'}`;
          setErrors({
            login: message,
            password: message,
          });
        }
        if (error.response.status === 403) {
          setModalMessage('Your account is under verification, our team will contact you as soon as possible.');
          setModalVisible(true);
        }
        const message = `Login or password is incorrect.${host === 'loc' ? ' Or something is wrong!' : ' Try again'}`;
        setErrors({
          login: message,
          password: message,
        });
      })
      .finally(() => setLoaderForm(false));
  };

  useEffect(() => {
    const flashCookieValue = getCookie('flash_message');
    const showModalFlag = localStorage.getItem('showModal');

    if (showModalFlag === 'true') {
      if (flashCookieValue && typeof flashCookieValue === 'string') {
        localStorage.setItem('showModal', 'true');
        const result = Buffer.from(flashCookieValue, 'base64').toString('utf8');
        setModalMessage(JSON.parse(result).message);
        setModalVisible(true);
        remove('flash_message');
      }
      localStorage.setItem('showModal', 'false');
    } else return;
  }, []);

  const modalHandler = () => (location.href = location.origin);

  return (
    <section className="log-in section__100vh">
      <form
        className={`log-in__form ${!!params ? '' : 'hidden'}`}
        onSubmit={(e: any) =>
          service(e)({
            Function: 'submitHandler()',
            Component: 'resources/js/Pages/Directory/LogIn/index.tsx',
            Line: 46,
            Author: '#Vlad&Vladimir',
          })(submitHandler)
        }
      >
        {/* TODO: refactoring needed */}
        {params?.login === 'first' ? (
          <h1 className="log-in__form-title">Sign In to DF Alliance</h1>
        ) : (
          <h1 className="log-in__form-title">Sign In to DF Alliance</h1>
        )}

        <Input
          name="login"
          placeholder="Login or Email"
          error={errors.login}
          setFormData={setFormData}
          setErrors={setErrors}
        />

        <Input
          name="password"
          placeholder="Password"
          error={errors.password}
          setFormData={setFormData}
          setErrors={setErrors}
          type="password"
        />

        <button className={`btn btn-dark btn-full ${loaderForm ? 'isDisabled' : ''}`}>
          {loaderForm ? <div className="contact__loader"/> : ''}
          Sign In
        </button>

        <p className="control__link signup_link">
          <span>Don't have an account yet?</span>
          <Link href="/auth/sign-up" className="accent_blue">
            Sign Up
          </Link>
        </p>

        <p className="control__link forgot_link">
          <Link href="/auth/forgot-password" className="accent_blue">
            Forgot password?
          </Link>
        </p>
      </form>

      <Modal
        title={modalMessage}
        text=""
        btnText="Main page"
        modalHandler={modalHandler}
        open={modalVisible}
        setModalVisible={setModalVisible}
        contact={''}
      />

      <div className="log-in__bg">
        <img src="/images/global/map.svg" alt="Map"/>
      </div>
    </section>
  );
}

// export const getStaticProps = async () => {
//   return {
//     props: {
//       title: 'Login Logistics Members Directory - Digital Freight Alliance',
//       description:
//         'Login to access our Logistics Member Directory. We have to Access Freight Forwarders in more than 150 Countries.',
//       keywords: 'logistics members directory',
//       path: '/auth/sign-in',
//     },
//   };
// }

export const getServerSideProps = withSessionSsr(async function ({req, res}) {
  const {user} = req.session;

  if (!user || !user.profile) {
    return {
      props: {
        title: 'Sign In to Your DF Alliance Account | DF Alliance',
        description:
          'Sign in to DF Alliance for easy access to all your global logistics and freight forwarding needs, including membership benefits and industry resources.',
        keywords: 'logistics members directory',
        path: '/auth/sign-in',
      },
    };
  }

  if (user || user.profile) {
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    };
  }

  return {
    props: {
      title: 'Sign In to Your DF Alliance Account | DF Alliance',
      description:
        'Sign in to DF Alliance for easy access to all your global logistics and freight forwarding needs, including membership benefits and industry resources.',
      keywords: 'logistics members directory',
      path: '/auth/sign-in',
    },
  };
});

LogInPage.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};
